<template>
    <div>
        <div v-if="loading && !list.length" class="py-8 flex justify-center">
            <vx-icon-loading class="w-8 h-8" spin />
        </div>

        <vx-box
            v-else-if="!list.length"
            color="info"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            No runs yet.
        </vx-box>

        <vx-table v-else class="xl:grid-cols-3-auto" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>Date</div>
                <div>URL</div>
            </vx-table-row>

            <vx-table-row v-for="run in list" :key="run.id">
                <vx-table-row-title>
                    <template v-slot:label>Date:</template>
                    {{ shortDateTime(run.created_at) }}
                </vx-table-row-title>

                <vx-table-row-item>
                    <template v-slot:label>URL:</template>
                    <vx-popover hover :arrow="true" placement="top" class="w-full">
                        <button
                            type="button"
                            class="block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                            data-test="url"
                        >
                            <span class="block truncate">{{ run.url }}</span>
                        </button>
                        <template v-slot:content>
                            {{ run.url }}
                        </template>
                    </vx-popover>
                </vx-table-row-item>

                <vx-table-row-buttons>
                    <vx-button
                        type="button"
                        color="muted-dark"
                        size="sm"
                        @click="showFilesModal(run)"
                        data-test="view-files-button"
                    >
                        View Files ({{ run.imported_files.length }})
                    </vx-button>
                </vx-table-row-buttons>
            </vx-table-row>
        </vx-table>

        <div v-if="fetchParams.cursor" class="mt-8">
            <vx-button @click="loadData()" color="muted-dark" size="xl" :loading="loading" data-test="load-more-button">
                Load More
            </vx-button>
        </div>

        <ftp-pull-files-modal v-model:visible="filesModalVisible" :run="selectedRun" />
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import {
    VxTable,
    VxTableRow,
    VxTableRowTitle,
    VxTableRowItem,
    VxTableRowButtons,
    VxBox,
    VxButton,
    VxIconLoading,
    VxPopover,
} from '@voxie/frontend-components';
import { useCursorPagination } from '~/composables/useCursorPagination';
import teamsClient from '~/services/teamsClient';
import { shortDateTime } from '~/utils/date';
import type { FtpPullRun } from '~/types/ftp-pull.types';
import FtpPullFilesModal from './FtpPullFilesModal.vue';

const teamId = Spark.state.currentTeam.id;

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<FtpPullRun>();

const filesModalVisible = ref(false);
const selectedRun = ref<FtpPullRun | null>(null);

const defaultFilters = () => ({
    per_page: 10,
    cursor: null,
});

const loadData = async () => {
    await fetchRecords(teamsClient.getFtpPullRuns(teamId, fetchParams.value));
};

const showFilesModal = (run: FtpPullRun) => {
    selectedRun.value = run;
    filesModalVisible.value = true;
};

onMounted(() => {
    fetchParams.value = defaultFilters();
    loadData();
});
</script>
